/**
 * Creates a safe wrapper around a Web Storage object that catches SecurityError
 * exceptions thrown by iOS Safari in restricted storage contexts.
 *
 * Accepts a getter function to lazily resolve the storage object — safe to create
 * at module scope. Storage is only accessed when a method is called; any error
 * (SecurityError, ReferenceError in Node/test) is caught per-invocation.
 */
export function createSafeStorage(getStorage: () => Storage): Storage {
    return {
        get length(): number {
            try {
                return getStorage().length;
            } catch {
                return 0;
            }
        },
        key(index: number): string | null {
            try {
                return getStorage().key(index);
            } catch {
                return null;
            }
        },
        getItem(key: string): string | null {
            try {
                return getStorage().getItem(key);
            } catch {
                return null;
            }
        },
        setItem(key: string, value: string): void {
            try {
                getStorage().setItem(key, value);
            } catch (e) {
                if (import.meta.env.DEV && e instanceof DOMException && e.name === 'QuotaExceededError') {
                    console.warn('[safeStorage] QuotaExceededError writing key:', key);
                }
            }
        },
        removeItem(key: string): void {
            try {
                getStorage().removeItem(key);
            } catch {
                // Storage unavailable — removal silently discarded
            }
        },
        clear(): void {
            try {
                getStorage().clear();
            } catch {
                // Storage unavailable — clear silently discarded
            }
        },
    };
}

export const safeLocalStorage: Storage = createSafeStorage(() => localStorage);
export const safeSessionStorage: Storage = createSafeStorage(() => sessionStorage);
